/**
 * Document protection — telling "Word refused" apart from "the AI refused".
 *
 * Observed live (2026-08-28): Optimize failed on a protected document with
 *
 *   RichApi.Error: You don't have sufficient permissions for this action.
 *
 * raised by Office.js during the engine's READ, before any model request. The
 * pane surfaced it as a bare permission error, which read as an access problem
 * with the AI service — costing a day of chasing gateway keys, LiteLLM team
 * scoping and model aliases, none of which were involved. The run log settled it
 * only because it showed the failure arriving before the `llm: start` line.
 *
 * The engine's read is not read-only: it plants a bookmark on every paragraph so
 * a later apply can find its target. On a protected document that write is
 * refused, which is correct behaviour badly explained.
 */
import { detectCapabilities } from './capabilities';

/** Word's protection modes that forbid the anchors and edits the engine needs. */
const BLOCKING_PROTECTION = new Set([
  'AllowOnlyComments',
  'AllowOnlyFormFields',
  'AllowOnlyReading',
]);

/**
 * `AllowOnlyRevisions` is NOT blocking: it forces every edit to be a tracked
 * change, which is exactly what the engine produces anyway.
 */
export function isBlockingProtection(protectionType: string | null | undefined): boolean {
  return !!protectionType && BLOCKING_PROTECTION.has(protectionType);
}

/** Plain-language name for a protection mode, for the message shown to the user. */
export function describeProtection(protectionType: string): string {
  switch (protectionType) {
    case 'AllowOnlyComments':
      return 'comments only';
    case 'AllowOnlyFormFields':
      return 'form fields only';
    case 'AllowOnlyReading':
      return 'read only';
    case 'AllowOnlyRevisions':
      return 'tracked changes only';
    default:
      return protectionType;
  }
}

/**
 * Is this an Office.js "you may not change this document" error?
 *
 * Matched on both the API error code and the message, because the code is absent
 * on some hosts and the message is localised on others — so neither alone is
 * reliable, and a missed match sends the user back to hunting an AI-service
 * problem that does not exist.
 */
export function isAccessDeniedError(error: unknown): boolean {
  const e = error as { code?: unknown; message?: unknown; name?: unknown } | null;
  if (!e) return false;
  const code = typeof e.code === 'string' ? e.code : '';
  const message = typeof e.message === 'string' ? e.message : '';
  if (code === 'AccessDenied' || code === 'GeneralException_AccessDenied') return true;
  return /sufficient permissions|access denied|not allowed to (?:edit|modify)|document is (?:protected|read[- ]only)/i.test(
    message,
  );
}

/** What the user is told when Word refuses. Names the LAYER, which is the whole
 *  point: the previous wording was indistinguishable from an AI-access error. */
export function protectedDocumentMessage(protectionType?: string | null): string {
  const mode = protectionType ? ` (currently: ${describeProtection(protectionType)})` : '';
  return (
    `Word will not let the add-in change this document${mode}. ` +
    'It is protected, open read-only, or in Protected View — this is not a problem with your AI access. ' +
    'Try: the yellow bar → “Enable Editing”; Review → Restrict Editing → “Stop Protection”; ' +
    'or save a local copy and work on that.'
  );
}

/**
 * The document's protection mode, or null when the host cannot report it.
 *
 * `protectionType` needs WordApiDesktop 1.4, so it is absent on Word for the web
 * and older desktop builds. Null therefore means "unknown", never "unprotected" —
 * callers must not treat it as permission to proceed confidently; the error path
 * remains the backstop.
 */
export async function readProtectionType(): Promise<string | null> {
  if (!detectCapabilities().documentFocus) return null; // same WordApiDesktop 1.4 gate
  try {
    let value: string | null = null;
    await Word.run(async (context) => {
      const doc = context.document as unknown as {
        load: (p: string) => void;
        protectionType?: string;
      };
      doc.load('protectionType');
      await context.sync();
      value = doc.protectionType ?? null;
    });
    return value;
  } catch {
    // Never let a diagnostic probe break the action it was meant to explain.
    return null;
  }
}
